import React from 'react';
import { AlertCircle, Info, CheckCircle, Calendar, Hash, FileText, DollarSign } from 'lucide-react';

/**
 * Sistema de mensagens de erro contextuais e educativas
 * Fornece orientações específicas para cada tipo de erro de importação
 */

/**
 * Mapeia tipos de erro para mensagens educativas com exemplos
 */
export const getErrorGuidance = (field, errorMessage) => {
  const errorLower = errorMessage.toLowerCase();
  
  // ==================== CNPJ ====================
  if (field === 'cnpj' || errorLower.includes('cnpj')) {
    if (errorLower.includes('obrigatório')) {
      return {
        icon: <AlertCircle className="w-5 h-5 text-red-600" />,
        title: 'CNPJ Obrigatório',
        problem: 'A célula do CNPJ está vazia.',
        solution: 'Preencha o CNPJ da organização.',
        examples: [
          { label: 'Formato completo', value: '12.345.678/0001-90', valid: true },
          { label: 'Apenas números', value: '12345678000190', valid: true },
          { label: 'Célula vazia', value: '(vazio)', valid: false }
        ],
        tip: 'O CNPJ pode ser preenchido com ou sem formatação (pontos, barra e hífen).'
      };
    }
    
    if (errorLower.includes('14 dígitos') || errorLower.includes('dígito')) {
      return {
        icon: <AlertCircle className="w-5 h-5 text-red-600" />,
        title: 'CNPJ com Tamanho Incorreto',
        problem: 'O CNPJ não possui 14 dígitos.',
        solution: 'Verifique se digitou todos os números do CNPJ.',
        examples: [
          { label: 'Correto (14 dígitos)', value: '12.345.678/0001-90', valid: true },
          { label: 'Incompleto (13 dígitos)', value: '12.345.678/0001-9', valid: false },
          { label: 'Com letras', value: '12.345.678/0001-9A', valid: false }
        ],
        tip: 'O CNPJ deve ter exatamente 14 números. Remova espaços e caracteres especiais extras.'
      };
    }
    
    if (errorLower.includes('inválido') || errorLower.includes('verificador')) {
      return {
        icon: <AlertCircle className="w-5 h-5 text-red-600" />,
        title: 'CNPJ Inválido',
        problem: 'Os dígitos verificadores do CNPJ estão incorretos.',
        solution: 'Verifique se o CNPJ foi digitado corretamente.',
        examples: [
          { label: 'CNPJ válido', value: '11.222.333/0001-81', valid: true },
          { label: 'Dígitos incorretos', value: '11.222.333/0001-99', valid: false },
          { label: 'Todos iguais', value: '11.111.111/1111-11', valid: false }
        ],
        tip: 'Copie o CNPJ diretamente do documento oficial da organização para evitar erros de digitação.'
      };
    }
  }

  // ==================== ANO ====================
  if (field === 'ano' || errorLower.includes('ano')) {
    if (errorLower.includes('obrigatório')) {
      return {
        icon: <Calendar className="w-5 h-5 text-red-600" />,
        title: 'Ano Obrigatório',
        problem: 'A célula do Ano está vazia.',
        solution: 'Preencha o ano da parceria (4 dígitos).',
        examples: [
          { label: 'Formato correto', value: '2024', valid: true },
          { label: 'Ano válido', value: '2023', valid: true },
          { label: 'Célula vazia', value: '(vazio)', valid: false }
        ],
        tip: 'Use apenas o ano com 4 dígitos, sem espaços ou caracteres especiais.'
      };
    }
    
    if (errorLower.includes('inválido') || errorLower.includes('entre 2000 e 2100')) {
      return {
        icon: <Calendar className="w-5 h-5 text-red-600" />,
        title: 'Ano Inválido',
        problem: 'O ano está fora do intervalo permitido (2000-2100).',
        solution: 'Use um ano entre 2000 e 2100.',
        examples: [
          { label: 'Ano válido', value: '2024', valid: true },
          { label: 'Ano válido', value: '2020', valid: true },
          { label: 'Muito antigo', value: '1999', valid: false },
          { label: 'Muito futuro', value: '2101', valid: false },
          { label: 'Formato errado', value: '24', valid: false }
        ],
        tip: 'Use o ano completo com 4 dígitos. Exemplo: 2024, não 24.'
      };
    }
  }

  // ==================== OSC ====================
  if (field === 'osc' || errorLower.includes('osc') || errorLower.includes('organização')) {
    return {
      icon: <FileText className="w-5 h-5 text-red-600" />,
      title: 'Nome da OSC Obrigatório',
      problem: 'A célula do nome da OSC está vazia.',
      solution: 'Preencha o nome completo da Organização da Sociedade Civil.',
      examples: [
        { label: 'Nome completo', value: 'Associação Beneficente São José', valid: true },
        { label: 'Nome com sigla', value: 'Instituto ABC - Ação Social', valid: true },
        { label: 'Célula vazia', value: '(vazio)', valid: false }
      ],
      tip: 'Use o nome oficial da organização conforme consta no CNPJ.'
    };
  }

  // ==================== DATAS ====================
  if (field === 'data_inicio' || field === 'data_fim' || errorLower.includes('data')) {
    if (errorLower.includes('inválida') || errorLower.includes('formato')) {
      const isDataInicio = field === 'data_inicio' || errorLower.includes('início');
      return {
        icon: <Calendar className="w-5 h-5 text-red-600" />,
        title: `Data ${isDataInicio ? 'de Início' : 'de Fim'} Inválida`,
        problem: 'A data não está em um formato reconhecido.',
        solution: 'Use o formato brasileiro DD/MM/AAAA (recomendado).',
        examples: [
          { label: 'Formato brasileiro (IDEAL)', value: '15/01/2024', valid: true },
          { label: 'Formato ISO', value: '2024-01-15', valid: true },
          { label: 'Sem separadores', value: '15012024', valid: true },
          { label: 'Formato incorreto', value: '2024/15/01', valid: false },
          { label: 'Data inexistente', value: '32/01/2024', valid: false },
          { label: 'Texto', value: 'Janeiro de 2024', valid: false }
        ],
        tip: 'Use preferencialmente o formato DD/MM/AAAA (ex: 15/01/2024). O Excel pode converter automaticamente se você digitar a data.'
      };
    }
  }

  // ==================== VALOR ====================
  if (field === 'valor' || errorLower.includes('valor')) {
    if (errorLower.includes('inválido') || errorLower.includes('positivo')) {
      return {
        icon: <DollarSign className="w-5 h-5 text-red-600" />,
        title: 'Valor Inválido',
        problem: 'O valor não é um número válido ou é negativo.',
        solution: 'Use números positivos com vírgula ou ponto para decimais.',
        examples: [
          { label: 'Formato brasileiro (IDEAL)', value: '1.000,50', valid: true },
          { label: 'Com vírgula', value: '50000,00', valid: true },
          { label: 'Com ponto', value: '50000.50', valid: true },
          { label: 'Apenas números', value: '50000', valid: true },
          { label: 'Com R$', value: 'R$ 50.000,00', valid: true },
          { label: 'Valor negativo', value: '-1000', valid: false },
          { label: 'Texto', value: 'Cinquenta mil', valid: false }
        ],
        tip: 'Use vírgula para decimais (ex: 1.000,50). O sistema aceita R$ mas remove automaticamente.'
      };
    }
  }

  // ==================== PROCESSO/ANO ====================
  if (field === 'processo_ano' || errorLower.includes('processo')) {
    return {
      icon: <Hash className="w-5 h-5 text-orange-600" />,
      title: 'Formato de Processo/Ano',
      problem: 'O formato do processo/ano pode não estar ideal.',
      solution: 'Use formatos como: 1234/2024, 1234-2024 ou apenas 1234.',
      examples: [
        { label: 'Com barra', value: '1234/2024', valid: true },
        { label: 'Com hífen', value: '1234-2024', valid: true },
        { label: 'Com espaço', value: '1234 2024', valid: true },
        { label: 'Apenas número', value: '1234', valid: true },
        { label: 'Vazio (opcional)', value: '(vazio)', valid: true }
      ],
      tip: 'Este campo é opcional. Se vazio, o sistema gera automaticamente "Processo/{ano}".'
    };
  }

  // ==================== ERRO GENÉRICO ====================
  return {
    icon: <AlertCircle className="w-5 h-5 text-red-600" />,
    title: `Erro no campo: ${field}`,
    problem: errorMessage,
    solution: 'Verifique o valor preenchido e corrija conforme as instruções do template.',
    examples: [],
    tip: 'Consulte a aba "Instruções" do template Excel para mais detalhes sobre este campo.'
  };
};

/**
 * Componente visual para exibir orientações de erro
 */
export const ErrorGuidanceCard = ({ field, errorMessage }) => {
  const guidance = getErrorGuidance(field, errorMessage);

  return (
    <div className="bg-white border-2 border-red-300 rounded-lg p-4 space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-start gap-3">
        {guidance.icon}
        <div className="flex-1">
          <h4 className="font-bold text-red-900 text-sm">{guidance.title}</h4>
          <p className="text-xs text-red-700 mt-1">
            <strong>Problema:</strong> {guidance.problem}
          </p>
        </div>
      </div>

      {/* Solução */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3">
        <div className="flex items-start gap-2">
          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-blue-900">Como Corrigir:</p>
            <p className="text-xs text-blue-800 mt-1">{guidance.solution}</p>
          </div>
        </div>
      </div>

      {/* Exemplos */}
      {guidance.examples.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700">Exemplos:</p>
          <div className="space-y-1">
            {guidance.examples.map((example, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-2 text-xs p-2 rounded ${
                  example.valid 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {example.valid ? (
                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0" />
                )}
                <span className={example.valid ? 'text-green-800' : 'text-red-800'}>
                  <strong>{example.label}:</strong> <code className="bg-white px-1 rounded">{example.value}</code>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dica */}
      {guidance.tip && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-yellow-900">💡 Dica:</p>
              <p className="text-xs text-yellow-800 mt-1">{guidance.tip}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorGuidanceCard;
